import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    History, X, RotateCcw, Trash2, Clock,
    ChevronRight, Loader2, AlertCircle, FileText
} from 'lucide-react';
import versionHistoryService from '../../../services/versionHistoryService';

/**
 * VersionHistoryPanel Component
 * 
 * Displays version history for episode plans with restore functionality.
 * Integrates with SmartEditor for version management.
 */
const VersionHistoryPanel = ({
    isOpen,
    onClose,
    episodeId,
    onRestoreVersion,
    currentContent
}) => {
    const [versions, setVersions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const [deletingVersionId, setDeletingVersionId] = useState(null);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

    // Fetch versions when panel opens
    useEffect(() => {
        if (isOpen && episodeId) {
            fetchVersions();
        }
    }, [isOpen, episodeId]);

    /**
     * Fetch version list
     */
    const fetchVersions = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await versionHistoryService.listVersions(episodeId);
            if (result.success) {
                setVersions(result.versions);
            }
        } catch (err) {
            console.error('Failed to fetch versions:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load version content for preview
     */
    const handlePreviewVersion = async (version) => {
        if (selectedVersion?.id === version.id) {
            setSelectedVersion(null);
            setPreviewContent(null);
            return;
        }

        setSelectedVersion(version);

        try {
            const result = await versionHistoryService.getVersion(version.id);
            if (result.success) {
                setPreviewContent(result.version.content);
            }
        } catch (err) {
            console.error('Failed to load version:', err);
            setError(err.message);
        }
    };

    /**
     * Restore a version
     */
    const handleRestore = async () => {
        if (!selectedVersion || !previewContent) return;

        setIsRestoring(true);
        setError(null);

        try {
            // Save current content as a version before restoring
            if (currentContent) {
                await versionHistoryService.saveVersion(
                    episodeId,
                    currentContent,
                    'Auto-saved before restore'
                );
            }

            // Call restore callback with the content
            if (onRestoreVersion) {
                onRestoreVersion(previewContent);
            }

            // Close panel after successful restore
            onClose();

        } catch (err) {
            console.error('Failed to restore version:', err);
            setError(err.message);
        } finally {
            setIsRestoring(false);
        }
    };

    /**
     * Delete a version
     */
    const handleDeleteVersion = async (versionId) => {
        // Prevent double deletion
        if (deletingVersionId === versionId) return;

        setDeletingVersionId(versionId);
        setError(null);

        try {
            const result = await versionHistoryService.deleteVersion(versionId);

            if (result.success) {
                // Update local state immediately for responsive UI
                setVersions(prev => prev.filter(v => v.id !== versionId));

                // Clear selection if the deleted version was selected
                if (selectedVersion?.id === versionId) {
                    setSelectedVersion(null);
                    setPreviewContent(null);
                }
            } else {
                throw new Error(result.error || 'Failed to delete version');
            }
        } catch (err) {
            console.error('Failed to delete version:', err);
            setError(err.message);
            // Refresh versions from server to ensure UI is in sync
            fetchVersions();
        } finally {
            setDeletingVersionId(null);
        }
    };

    /**
     * Bulk delete all versions
     */
    const handleBulkDeleteAll = async () => {
        if (!episodeId || isBulkDeleting) return;

        setIsBulkDeleting(true);
        setError(null);
        setShowBulkDeleteConfirm(false);

        try {
            const result = await versionHistoryService.bulkDeleteVersions(episodeId);

            if (result.success) {
                // Clear all versions from local state
                setVersions([]);
                setSelectedVersion(null);
                setPreviewContent(null);
            } else {
                throw new Error(result.error || 'Failed to delete all versions');
            }
        } catch (err) {
            console.error('Failed to bulk delete versions:', err);
            setError(err.message);
            // Refresh versions from server to ensure UI is in sync
            fetchVersions();
        } finally {
            setIsBulkDeleting(false);
        }
    };

    /**
     * Format timestamp for display
     */
    const formatTimestamp = (timestamp) => {
        return versionHistoryService.formatRelativeTime(timestamp);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
                    />

                    {/* Panel */}
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed top-0 left-0 h-full w-full sm:w-[400px] bg-white border-r-2 border-slate-100 shadow-2xl z-50 flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-amber-100 text-amber-600">
                                    <History size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit']">Version History</h3>
                                    <p className="text-xs text-slate-500 font-medium">
                                        {versions.length} version{versions.length !== 1 ? 's' : ''} saved
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-900 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Error Alert */}
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4 pt-3"
                                >
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                                        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700">{error}</p>
                                        <button
                                            onClick={() => setError(null)}
                                            className="text-red-400 hover:text-red-600 ml-auto"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Bulk Delete Confirmation */}
                        <AnimatePresence>
                            {showBulkDeleteConfirm && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4 pt-3"
                                >
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                        <div className="flex items-start gap-2 mb-3">
                                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-red-700">Delete All Versions?</p>
                                                <p className="text-xs text-red-600 mt-1">
                                                    This will permanently delete all {versions.length} version(s). This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setShowBulkDeleteConfirm(false)}
                                                className="flex-1 py-2 px-3 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleBulkDeleteAll}
                                                disabled={isBulkDeleting}
                                                className="flex-1 py-2 px-3 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                                            >
                                                {isBulkDeleting ? (
                                                    <Loader2 size={12} className="animate-spin" />
                                                ) : (
                                                    <Trash2 size={12} />
                                                )}
                                                Delete All
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Version List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-32">
                                    <Loader2 size={24} className="text-amber-500 animate-spin" />
                                </div>
                            ) : versions.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400">
                                    <Clock size={32} className="mb-2 opacity-50" />
                                    <p className="text-sm font-medium">No versions yet</p>
                                    <p className="text-xs mt-1">Versions are saved automatically</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {versions.map((version, index) => (
                                        <div key={version.id}>
                                            <button
                                                onClick={() => handlePreviewVersion(version)}
                                                disabled={deletingVersionId === version.id}
                                                className={`w-full text-left p-3 rounded-xl border transition-all ${selectedVersion?.id === version.id
                                                    ? 'border-amber-300 bg-amber-50'
                                                    : 'border-slate-100 hover:border-amber-200 hover:bg-slate-50'
                                                    } ${deletingVersionId === version.id ? 'opacity-50' : ''}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-1.5 rounded-lg ${selectedVersion?.id === version.id
                                                            ? 'bg-amber-200 text-amber-700'
                                                            : 'bg-slate-100 text-slate-500'
                                                            }`}>
                                                            {deletingVersionId === version.id ? (
                                                                <Loader2 size={14} className="animate-spin" />
                                                            ) : (
                                                                <FileText size={14} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-slate-700">
                                                                {version.description || `Version ${versions.length - index}`}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <Clock size={10} className="text-slate-400" />
                                                                <span className="text-[10px] text-slate-400">
                                                                    {formatTimestamp(version.createdAt)}
                                                                </span>
                                                                <span className="text-[10px] text-slate-300">•</span>
                                                                <span className="text-[10px] text-slate-400">
                                                                    {version.contentLength || 0} chars
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ChevronRight
                                                        size={16}
                                                        className={`text-slate-400 transition-transform ${selectedVersion?.id === version.id ? 'rotate-90' : ''
                                                            }`}
                                                    />
                                                </div>
                                            </button>

                                            {/* Preview */}
                                            <AnimatePresence>
                                                {selectedVersion?.id === version.id && previewContent && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                                                            <p className="text-xs text-slate-500 mb-2 font-medium">Preview:</p>
                                                            <div className="max-h-32 overflow-y-auto">
                                                                <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                                                                    {previewContent.slice(0, 500)}
                                                                    {previewContent.length > 500 && '...'}
                                                                </pre>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                                                                <button
                                                                    onClick={handleRestore}
                                                                    disabled={isRestoring}
                                                                    className="flex-1 py-2 px-3 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                                                                >
                                                                    {isRestoring ? (
                                                                        <Loader2 size={12} className="animate-spin" />
                                                                    ) : (
                                                                        <RotateCcw size={12} />
                                                                    )}
                                                                    Restore
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteVersion(version.id)}
                                                                    disabled={deletingVersionId === version.id}
                                                                    className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                                                    title="Delete version"
                                                                >
                                                                    {deletingVersionId === version.id ? (
                                                                        <Loader2 size={14} className="animate-spin" />
                                                                    ) : (
                                                                        <Trash2 size={14} />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer with Bulk Delete */}
                        <div className="p-4 bg-slate-50 border-t border-slate-100">
                            {versions.length > 0 && (
                                <button
                                    onClick={() => setShowBulkDeleteConfirm(true)}
                                    disabled={isBulkDeleting || showBulkDeleteConfirm}
                                    className="w-full py-2 px-3 mb-3 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 text-xs font-medium rounded-lg flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                                >
                                    <Trash2 size={14} />
                                    Delete All Versions
                                </button>
                            )}
                            <p className="text-[10px] text-slate-400 text-center">
                                Versions are automatically saved every 15 minutes
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default VersionHistoryPanel;
